import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import React, { useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

interface ResponseExample {
  status: string;
  description?: string;
  response: string;
}

interface ApiResponseExamplesProps {
  examples: ResponseExample[];
  title?: string;
}
const highlightJson = (jsonString: string) => {
  const cleanJson = jsonString.trim();
  return cleanJson
    .replace(/"([^"]+)":/g, '<span style="color: #82bcf7ff">"$1"</span>:')
    .replace(/: "([^"]*)"/g, ': <span style="color: #a3de7cff">"$1"</span>')
    .replace(/: ([0-9]+)/g, ': <span style="color: #fab387">$1</span>')
    .replace(/: (true|false)/g, ': <span style="color: #fab387">$1</span>')
    .replace(/: (null)/g, ': <span style="color: #eb6a8eff">$1</span>')
    .replace(/({|})/g, '<span style="color: #cdd6f4">$1</span>')
    .replace(/(\[|\])/g, '<span style="color: #cdd6f4">$1</span>')
    .replace(/,/g, '<span style="color: #cdd6f4">,</span>');
};

const ApiResponseExamples: React.FC<ApiResponseExamplesProps> = ({ examples, title = "Response Examples" }) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleCopy = (response: string, index: number) => {
    let textToCopy;
    try {
      const parsed = JSON.parse(response);
      textToCopy = JSON.stringify(parsed, null, 2);
    } catch {
      textToCopy = response;
    }
    
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  };

  return (
    <>
      <Typography variant="body2" fontSize={14} fontWeight={600} mb={1}>
        {title}
      </Typography>
      {examples.map((example, index) => {
        let formattedJson;
        try {
          const parsed = JSON.parse(example.response);
          formattedJson = JSON.stringify(parsed, null, 2);
        } catch {
          formattedJson = example.response;
        }

        return (
          <Box key={index} sx={{ mb: 3 }}>
            <Typography 
              variant="body2" 
              fontSize={12} 
              fontWeight={600} 
              mb={1}
              sx={{ color: '#6b7280' }}
            >
              {example.status}{example.description && ` - ${example.description}`}
            </Typography>
            
            <Box sx={{ position: 'relative' }}>
              <Box 
                sx={{ 
                  backgroundColor: '#393942ff',
                  border: '1px solid #45475a',
                  borderRadius: 2,
                  overflow: 'hidden'
                }}
              >
                <Box 
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#2e2e34ff',
                    borderBottom: '1px solid #45475a',
                    px: 2,
                    py: 1
                  }}
                >
                  <Typography 
                    variant="body2" 
                    fontSize={12}
                    sx={{ 
                      color: '#a6adc8',
                      fontWeight: 500
                    }}
                  >
                    json
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={copiedIndex === index ? "Copied!" : "Copy"} arrow>
                      <IconButton 
                        size="small" 
                        onClick={() => handleCopy(example.response, index)}
                        sx={{ 
                          color: '#a6adc8',
                          '&:hover': { 
                            backgroundColor: '#313244',
                            color: '#cdd6f4' 
                          }
                        }}
                      >
                        {copiedIndex === index ? 
                          <CheckIcon fontSize="small" /> : 
                          <ContentCopyIcon fontSize="small" />
                        }
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box 
                  component="pre"
                  sx={{ 
                    margin: 0,
                    padding: '16px',
                    fontSize: '13px',
                    lineHeight: 1.5,
                    fontFamily: '"Fira Code", "SF Mono", Monaco, Inconsolata, "Roboto Mono", monospace',
                    color: '#cdd6f4',
                    backgroundColor: '#383842ff',
                    overflow: 'auto',
                    whiteSpace: 'pre',
                    userSelect: 'text',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      height: '8px'
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#36363eff'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#45475a',
                      borderRadius: '4px'
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      backgroundColor: '#585b70'
                    }
                  }}
                  dangerouslySetInnerHTML={{
                    __html: highlightJson(formattedJson)
                  }}
                />
              </Box>
            </Box>
          </Box>
        );
      })}
    </>
  );
};

export default ApiResponseExamples;
